/**
 * Meta Pixel event tracking helpers.
 *
 * The base pixel is initialized in app/layout.tsx with Pixel ID 976247321887105.
 * These helpers wrap `window.fbq('track', ...)` calls with SSR-safe guards.
 *
 * All helpers are no-ops on the server and no-ops if fbq has not loaded yet.
 * Failures never throw — pixel events should never break the user flow.
 */

type EventParams = Record<string, unknown>;

function track(eventName: string, params?: EventParams): void {
  if (typeof window === 'undefined') return;
  if (typeof window.fbq !== 'function') return;
  try {
    if (params) {
      window.fbq('track', eventName, params);
    } else {
      window.fbq('track', eventName);
    }
  } catch {
    // Silent fail — never break the app over a pixel event.
  }
}

/**
 * Fired when a user views the sample Brief preview section on the homepage.
 * Signals interest in the product output. Used for retargeting and lookalike seeding.
 */
export function trackViewSampleBrief(): void {
  track('ViewContent', {
    content_name: 'Sample Brief',
    content_category: 'preview',
  });
}

/**
 * Fired when a user opens the path-selection bottom sheet from any of the
 * "Start one conversation free" CTAs on the homepage.
 * Top-of-funnel intent signal.
 */
export function trackInitiateCheckout(source: 'hero' | 'final_cta' | 'sticky_header'): void {
  track('InitiateCheckout', {
    content_name: 'Path Selection Sheet',
    source,
  });
}

/**
 * Fired when a user selects a path (engaged / married) in the bottom sheet.
 * This is the strongest pre-account-creation intent signal we have.
 * Used as the primary Lead optimization event.
 */
export function trackPathSelected(path: 'engaged' | 'married' | 'solo' | 'drift' | string): void {
  track('Lead', {
    content_name: 'Path Selected',
    path,
  });
}

/**
 * Fired immediately after a successful account creation via createCoupleWithPath.
 * Maps to the moment the couples + partners + assessment_cycles rows are written.
 */
export function trackCompleteRegistration(path?: string): void {
  track('CompleteRegistration', {
    content_name: 'Account Created',
    ...(path ? { path } : {}),
  });
}

/**
 * Fired the first time a partner accepts their invite token and lands in the app.
 * Strong indicator of viral coefficient and shared engagement.
 */
export function trackPartnerAcceptedInvite(): void {
  track('StartTrial', {
    content_name: 'Partner Accepted Invite',
  });
}

/**
 * Fired the first time a user views their completed section Brief.
 * This is the closest event to product-market-fit completion for free users.
 * Maps to the moment section_results.viewed_at is written.
 */
export function trackFirstBriefViewed(domain: string): void {
  track('Subscribe', {
    content_name: 'First Brief Viewed',
    content_category: domain,
  });
}

/**
 * Fired on the Stripe checkout success page.
 * The actual revenue conversion event. Highest-value optimization target.
 */
export function trackPurchase(params?: { value?: number; currency?: string }): void {
  track('Purchase', {
    content_name: 'Upgrade',
    value: params?.value ?? 0,
    currency: params?.currency ?? 'USD',
  });
}
